/* eslint-disable max-lines, max-lines-per-function */
import { useArtworkForm, useClearManualEdits } from "@design-lab/entities/artwork";
import {
  currentPinningSessionAtom,
  designProjectPlacementIdsAtom,
  getPinningSessionForPlacement,
  isSessionFinished,
  pendingInitialMessageAtom,
  pinningEnabledArtworksAtom,
  pinningModeAtom,
  pinningSessionsAtom,
  useCancelPinningSession,
  useCreatePinningSession
} from "@design-lab/entities/pinning";
import { usePlacementKey } from "@design-lab/features/design-artwork/model/usePlacementKey";
import { useDebouncedCallback } from "common/hooks/useDebounce";
import {
  trackDesignLabPinningModeEntered,
  trackDesignLabPinsCtaClicked,
  trackPinningSessionEntryInput
} from "@design-lab/shared/lib/analytics";
import { usePinBasedRedrawEnabled } from "@design-lab/shared/lib/config";
import { startPinSessionRecording } from "@design-lab/shared/lib/pinSessionRecording";
import { useToast } from "@design-lab/shared/ui";
import { Button } from "@design-lab/shared/ui/design-system/Button";
import { Textarea } from "@design-lab/shared/ui/textarea";
import { camelToSnakeCase } from "common/utils/helpers/cases";
import type { models } from "jiffy-api";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useRef, useState, type KeyboardEvent } from "react";
import { ArtistEditsHeader } from "./ArtistEditsHeader";
import { CompletedSessionIndicator } from "./CompletedSessionIndicator";

type RequestDesignChangesProps = {
  placementSide?: "front" | "back";
  currentValue?: models.cart.madeAdd.EnhancementTier | undefined;
  removeBackground?: boolean;
  canRemoveBackground?: boolean;
  editImageManual?: boolean;
};

function resolvePreviewImageName({
  currentValue,
  removeBackground,
  canRemoveBackground,
  editImageManual
}: {
  currentValue: models.cart.madeAdd.EnhancementTier | undefined;
  removeBackground: boolean;
  canRemoveBackground: boolean;
  editImageManual: boolean;
}): string | undefined {
  if (!currentValue) return undefined;
  if (currentValue === "print_original") return "original_image";
  if (currentValue === "full_enhancements") {
    if (editImageManual) return "manual_edit_image";
    if (removeBackground && canRemoveBackground) return "image_no_background";
    return "image_with_background";
  }
  return undefined;
}

function resolvePostClearPreviewImageName(
  currentValue: models.cart.madeAdd.EnhancementTier | undefined,
  removeBackground: boolean,
  canRemoveBackground: boolean
): string | undefined {
  if (!currentValue) return undefined;
  if (currentValue === "print_original") return "original_image";
  if (removeBackground && canRemoveBackground) return "image_no_background";
  return "image_with_background";
}

function focusTextareaOnContainerClick(
  e: React.MouseEvent<HTMLDivElement>,
  textareaRef: React.RefObject<HTMLTextAreaElement | null>
) {
  if ((e.target as HTMLElement).closest("button, input, label, a")) return;
  const interactiveElements = Array.from(
    e.currentTarget.querySelectorAll("button, input, label, a")
  );
  for (const el of interactiveElements) {
    const rect = el.getBoundingClientRect();
    if (
      e.clientX >= rect.left &&
      e.clientX <= rect.right &&
      e.clientY >= rect.top &&
      e.clientY <= rect.bottom
    ) {
      return;
    }
  }
  textareaRef.current?.focus();
}

export function RequestDesignChanges({
  placementSide = "front",
  currentValue,
  removeBackground = false,
  canRemoveBackground = true,
  editImageManual = false
}: RequestDesignChangesProps) {
  const { form } = useArtworkForm();
  const clearManualEdits = useClearManualEdits();
  const { mutate: cancelSession } = useCancelPinningSession();
  const setPinningMode = useSetAtom(pinningModeAtom);
  const [pinningSessions, setPinningSessions] = useAtom(pinningSessionsAtom);
  const designProjectPlacementIds = useAtomValue(designProjectPlacementIdsAtom);
  const setCurrentPinningSession = useSetAtom(currentPinningSessionAtom);
  const setPendingInitialMessage = useSetAtom(pendingInitialMessageAtom);
  const { mutate: createPinningSession, isPending: isCreatingPinningSession } =
    useCreatePinningSession();
  const { error: showErrorToast } = useToast();
  const pinBasedRedrawEnabled = usePinBasedRedrawEnabled();
  const pinningEnabledArtworks = useAtomValue(pinningEnabledArtworksAtom);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [inputMessage, setInputMessage] = useState("");

  const recordingTriggered = useRef(false);

  const placementKey = usePlacementKey(placementSide);
  const placementData = form.watch(placementKey);
  const artworkIdForm = (placementData?.artworkId ? String(placementData.artworkId) : "") as string;

  const trackEntryInput = useCallback(
    (inputValue: string) => {
      if (artworkIdForm) {
        trackPinningSessionEntryInput({
          artworkId: artworkIdForm,
          placement: camelToSnakeCase(placementKey),
          inputValue
        });
      }
    },
    [artworkIdForm, placementKey]
  );

  const debouncedTrackEntryInput = useDebouncedCallback(trackEntryInput, 500);

  // Check if pinning is enabled for this artwork (based on ownership)
  // If artworkId is not in the map, assume enabled (backwards compatibility)
  const isPinningEnabled = artworkIdForm ? (pinningEnabledArtworks[artworkIdForm] ?? true) : true;

  const { designProjectPlacementId, session: sessionForPlacement } = getPinningSessionForPlacement({
    placementKey,
    designProjectPlacementIds,
    pinningSessions,
    artworkId: artworkIdForm || undefined
  });
  const hasActiveSession =
    pinBasedRedrawEnabled &&
    (sessionForPlacement?.state === "active" ||
      sessionForPlacement?.state === "generating_preview" ||
      sessionForPlacement?.state === "preview_ready");
  const hasCompletedSession = isSessionFinished(sessionForPlacement?.state);

  const getPreviewImageName = useCallback(
    () =>
      resolvePreviewImageName({
        currentValue,
        removeBackground,
        canRemoveBackground,
        editImageManual
      }),
    [currentValue, removeBackground, canRemoveBackground, editImageManual]
  );

  const createNewPinningSession = useCallback(
    ({
      message,
      artworkId,
      sessionKey,
      previewImageName
    }: {
      message: string;
      artworkId: string;
      sessionKey: string;
      previewImageName: string | undefined;
    }) => {
      const sessionParams = {
        ...(designProjectPlacementId && { designProjectPlacementId }),
        artworkId,
        ...(previewImageName && { previewImageName })
      };

      createPinningSession(sessionParams, {
        onSuccess: (data: { sessionId: string }) => {
          trackDesignLabPinningModeEntered({
            artworkId,
            sessionId: data.sessionId,
            hasExistingPins: false,
            initialMessage: message
          });
          setPendingInitialMessage(message);
          startPinSessionRecording({ enabled: true });
          setPinningSessions((prev: typeof pinningSessions) => ({
            ...prev,
            [sessionKey]: {
              sessionId: data.sessionId,
              state: "active",
              summary: null
            }
          }));
          setCurrentPinningSession({
            designProjectPlacementId: sessionKey,
            artworkId,
            sessionId: data.sessionId
          });
          setPinningMode(true);
        },
        onError: () => {
          showErrorToast({
            title: "Unable to start design instructions",
            description: "Something went wrong. Please try again."
          });
        }
      });
    },
    [
      designProjectPlacementId,
      createPinningSession,
      setPinningSessions,
      setCurrentPinningSession,
      setPinningMode,
      setPendingInitialMessage,
      showErrorToast
    ]
  );

  const handleRequestDesignerEdit = useCallback(
    (message: string) => {
      if (isCreatingPinningSession) return;
      const artworkId = artworkIdForm;
      if (!artworkId) return;

      // Use artworkId as fallback key when no design project placement exists
      // (e.g. new placement added during edit mode)
      const sessionKey = designProjectPlacementId || artworkId;

      const previewImageName = getPreviewImageName();

      trackDesignLabPinsCtaClicked({ artworkId });

      // Reuse existing active session if one exists for this artwork
      const existingSession = sessionForPlacement;
      if (existingSession?.state === "active") {
        trackDesignLabPinningModeEntered({
          artworkId,
          sessionId: existingSession.sessionId,
          hasExistingPins: true,
          initialMessage: message
        });
        setPendingInitialMessage(message);
        startPinSessionRecording({ enabled: true });
        setCurrentPinningSession({
          designProjectPlacementId: sessionKey,
          artworkId,
          sessionId: existingSession.sessionId
        });
        setPinningMode(true);
        return;
      }

      // Cancel existing finished session before starting a new one
      if (existingSession && isSessionFinished(existingSession.state)) {
        cancelSession(
          { sessionId: existingSession.sessionId, artworkId },
          {
            onSuccess: () => {
              setPinningSessions(prev => {
                const next = { ...prev };
                delete next[sessionKey];
                return next;
              });
              clearManualEdits(placementSide);
              createNewPinningSession({
                message,
                artworkId,
                sessionKey,
                previewImageName: resolvePostClearPreviewImageName(
                  currentValue,
                  removeBackground,
                  canRemoveBackground
                )
              });
            },
            onError: () => {
              showErrorToast({
                title: "Unable to start new edit session",
                description: "Could not cancel the previous session. Please try again."
              });
            }
          }
        );
        return;
      }

      createNewPinningSession({ message, artworkId, sessionKey, previewImageName });
    },
    [
      isCreatingPinningSession,
      artworkIdForm,
      designProjectPlacementId,
      getPreviewImageName,
      sessionForPlacement,
      cancelSession,
      setPinningSessions,
      clearManualEdits,
      placementSide,
      currentValue,
      removeBackground,
      canRemoveBackground,
      createNewPinningSession,
      setPendingInitialMessage,
      setCurrentPinningSession,
      setPinningMode,
      showErrorToast
    ]
  );

  const handleContainerClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => focusTextareaOnContainerClick(e, textareaRef),
    []
  );

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const { value } = e.target;
      setInputMessage(value);
      debouncedTrackEntryInput(value);
      if (value.trim() && !recordingTriggered.current) {
        recordingTriggered.current = true;
        startPinSessionRecording({ enabled: true, forceMixpanelRecording: true });
      }
    },
    [debouncedTrackEntryInput]
  );

  const handleSubmitMessage = useCallback(() => {
    const trimmed = inputMessage.trim();
    if (!trimmed) return;
    handleRequestDesignerEdit(trimmed);
  }, [inputMessage, handleRequestDesignerEdit]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && !e.shiftKey && !e.metaKey && !e.ctrlKey) {
        e.preventDefault();
        handleSubmitMessage();
      }
    },
    [handleSubmitMessage]
  );

  if (hasCompletedSession) {
    return <CompletedSessionIndicator placementKey={placementKey} placementSide={placementSide} />;
  }

  if (!pinBasedRedrawEnabled || hasActiveSession || !isPinningEnabled) {
    return null;
  }

  return (
    <div className="mb-4">
      <div className="rounded-t-lg bg-[#AD47FF]/20 px-3 pb-4 pt-[3px]">
        <ArtistEditsHeader title="Edit with AI" />
      </div>
      <div
        className="gradient-border-purple-blue relative z-10 -mt-3 flex cursor-text flex-col gap-2 rounded-lg border-[1.5px] border-transparent px-3 py-2"
        onClick={handleContainerClick}
      >
        <Textarea
          ref={textareaRef}
          data-testid="request-designer-edit-input"
          data-hj-allow
          className="mp-unmask min-h-0 resize-none border-0 p-0 shadow-none focus-visible:ring-0"
          placeholder="Tell us what you'd like changed."
          rows={2}
          value={inputMessage}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isCreatingPinningSession}
        />
        <div className="flex cursor-pointer justify-end">
          <Button
            data-testid="request-designer-edit-submit"
            variant="link"
            size="sm"
            className={`bg-gradient-purple-blue !h-8 min-w-12 !rounded-lg !px-2 !text-xs !font-semibold !leading-4 !text-white !no-underline !shadow-none ${!inputMessage.trim() && !isCreatingPinningSession ? "opacity-50" : ""}`}
            onPress={handleSubmitMessage}
            isDisabled={isCreatingPinningSession}
          >
            {isCreatingPinningSession ? "Launching\u2026" : "Launch"}
          </Button>
        </div>
      </div>
    </div>
  );
}
